import {
  IconBarcode,
  IconBuildingWarehouse,
  IconClipboardList,
  IconFileText,
  IconLayoutGrid,
  IconMapPin,
  IconPackage,
  IconStack,
  IconTruck,
  IconUser,
} from "@tabler/icons-react";

import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";

function StockRequestDonePage() {
  const { t } = useTranslation();
  const [stockRequestDoneData, setStockRequestDoneData] = useState<any>({
    company: localStorage.getItem("companyId"),
    name: "",
    remarks: "",
    deliveryPerson: "",
    warehouseStaff: "",
  });
  const [productList, setProductList] = useState<any[]>([]);
  const [warehouseMap, setWarehouseMap] = useState<any>({});
  const [areaMap, setAreaMap] = useState<any>({});
  const [employeeData, setEmployeeData] = useState<any[]>([]);

  const { id } = useParams();
  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const navigate = useNavigate();

  const { name, remarks, deliveryPerson, warehouseStaff, warehouseStaffId } =
    stockRequestDoneData || {};

  const warehouseStaffName =
    warehouseStaff?.name ||
    employeeData.find((e) => e?.id === warehouseStaffId)?.name ||
    "";

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await single(orpcClient.stockRequest.read([{ id: id! }]));
        setStockRequestDoneData(result);

        const productList = result?.stockRequestProduct || [];
        setProductList(productList);

        const warehouses = await orpcClient.warehouse.list({});
        const areaToWarehouse: any = {};
        const areaToArea: any = {};
        await Promise.all(
          (warehouses ?? []).map(async (warehouse) => {
            const areas = await orpcClient.warehouseArea.list({
              filter: { warehouseId: warehouse?.id },
            });
            (areas ?? []).forEach((area) => {
              if (area?.id) {
                areaToWarehouse[area.id] = warehouse;
                areaToArea[area.id] = area;
              }
            });
          })
        );

        setWarehouseMap(areaToWarehouse);
        setAreaMap(areaToArea);
      } catch (error) {
        console.error("Error fetching stock request data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const result = await orpcClient.employee.list({});
        setEmployeeData(result);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchEmployees();
  }, []);

  const renderField = (
    label: string,
    value: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
        {value || <span className="text-gray-400">—</span>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {name || t("stockRequest.stockRequestName") || "領料單"}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("stockRequest.stockRequestDoc") || "領料單"}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("stockRequest.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
            {renderField(
              t("stockRequest.stockRequestName") || "領料單名稱",
              name,
              IconClipboardList
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPackage size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("stockRequest.productList") || "商品清單"}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            {productList?.length ? (
              productList.map((item, i) => (
                <div
                  className="rounded-lg border border-gray-200 p-4"
                  key={item?.id ?? i}
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {/* {renderField(
                      t("stockRequest.productCode") || "商品編號",
                      item?.product?.code,
                      IconBarcode
                    )} */}
                    {renderField(
                      t("stockRequest.productName") || "商品名稱",
                      item?.product?.name,
                      IconPackage
                    )}
                    {renderField(
                      t("stockRequest.warehouseLabel") || "倉庫",
                      warehouseMap[item?.storageLocation?.warehouseAreaId]
                        ?.name,
                      IconBuildingWarehouse
                    )}
                    {renderField(
                      t("stockRequest.areaLabel") || "區",
                      areaMap[item?.storageLocation?.warehouseAreaId]?.name,
                      IconLayoutGrid
                    )}
                    {renderField(
                      t("stockRequest.storageLocationLabel") || "儲位",
                      item?.storageLocation?.name,
                      IconMapPin
                    )}
                    {renderField(
                      t("stockRequest.quantity") || "數量",
                      item?.amount,
                      IconStack
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-400">
                —
              </div>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconTruck size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("stockRequest.deliveryInfo") || "交貨資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
            {renderField(
              t("stockRequest.requestPerson") || "領料人員",
              deliveryPerson,
              IconUser
            )}
            {renderField(
              t("stockRequest.outboundStaff") || "出庫人員",
              warehouseStaffName,
              IconTruck
            )}
            {renderField(
              t("stockRequest.remarks") || "備註",
              remarks,
              IconFileText
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default StockRequestDonePage;
